import { format as dateFnsFormat, startOfDay, endOfDay } from "date-fns";
import { ka, enUS } from "date-fns/locale";
import { toZonedTime } from "date-fns-tz";

const TBILISI_TZ = "Asia/Tbilisi";

type Language = "en" | "ka";

const locales = {
  en: enUS,
  ka: ka,
};

/**
 * Format date with language-aware locale
 */
export const formatDate = (
  date: Date | string | number,
  formatStr: string,
  language: Language = "en"
): string => {
  const dateObj = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  return dateFnsFormat(dateObj, formatStr, { locale: locales[language] });
};

/**
 * Format date for display in tables (short format)
 */
export const formatTableDate = (date: Date | string, language: Language = "en"): string => {
  return formatDate(date, "dd.MM.yyyy", language);
};

/**
 * Format time for display
 */
export const formatTime = (date: Date | string, language: Language = "en"): string => {
  return formatDate(date, "HH:mm", language);
};

/**
 * Format date and time together
 */
export const formatDateTime = (date: Date | string, language: Language = "en"): string => {
  return formatDate(date, "dd.MM.yyyy HH:mm", language);
};

/**
 * Format date for detailed view (long format)
 */
export const formatDetailDate = (date: Date | string, language: Language = "en"): string => {
  return formatDate(date, "dd.MM.yyyy", language);
};

/**
 * Format date for date range display
 */
export const formatDateRange = (date: Date | string, language: Language = "en"): string => {
  return formatDate(date, "dd.MM.yyyy", language);
};

/**
 * Convert a date range to Tbilisi-anchored ISO boundaries.
 * Ensures date filters align with Asia/Tbilisi midnight regardless of client timezone.
 */
export function toTbilisiDayRange(from: Date, to?: Date): { fromISO: string; toISO: string } {
  const tbilisiFrom = toZonedTime(from, TBILISI_TZ);
  const tbilisiTo = toZonedTime(to || from, TBILISI_TZ);
  return {
    fromISO: startOfDay(tbilisiFrom).toISOString(),
    toISO: endOfDay(tbilisiTo).toISOString(),
  };
}
